import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  ChartBarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ClientQualityGraph = ({ filters }) => {
  const [focus, setFocus] = useState(false);
  const { accessToken } = useAuth();
  const chartRef = useRef(null);

  const [labels, setLabels] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClientQualityData = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });

        const response = await api.get(
          `${baseUrl}/superAdminDashboard/getSuperAdminClientWiseQualityCount`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filteredParams,
          }
        );

        if (response.data.success && response.data.data) {
          const fetchedLabels = response.data.data.currentMonthData.map(item => item._id);
          const fetchedData = response.data.data.currentMonthData.map(item => item.qualityPercentage);
          setLabels(fetchedLabels);
          setRawData(fetchedData);
        } else {
          console.error('Failed to fetch Client Quality data');
        }
      } catch (error) {
        console.error('Error fetching Client Quality data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchClientQualityData();
  }, [filters, accessToken]);

  const noData = rawData.length === 0 || rawData.every((val) => val === 0 || val == null);

  const data = {
    labels,
    datasets: [
      {
        label: 'Quality %',
        data: rawData,
        backgroundColor: labels.map((_, index) => index % 2 === 0 ? '#1A2B5C' : '#EE1D23'),
        borderRadius: 14,
        barThickness: 36,
        borderSkipped: false,
        hoverBackgroundColor: labels.map((_, index) => index % 2 === 0 ? '#EE1D23' : '#1A2B5C'),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeOutQuint',
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#F3F4F6',
        titleColor: '#1A2B5C',
        bodyColor: '#374151',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 10,
        callbacks: {
          title: (ctx) => `Client: ${ctx[0].label}`,
          label: (ctx) => `Quality %: ${Math.round(ctx.raw)}%`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#1A2B5C',
          font: { size: 12, weight: 'bold' },
          callback: (val) => labels[val]?.toUpperCase().slice(0, 10) || '',
        },
        grid: { display: false },
      },
      y: {
        min: 80,
        max: 100,
        ticks: {
          stepSize: 5,
          color: '#4B5563',
          callback: (value) => `${value}%`,
        },
        grid: {
          color: '#E5E7EB',
          borderDash: [2, 4],
        },
      },
    },
  };

  const LoadingSkeleton = () => (
    <div className="flex flex-col items-center justify-center h-full animate-pulse text-center gap-4">
      <div className="w-3/4 h-5 rounded-md bg-gray-200" />
      <div className="w-1/2 h-4 rounded-md bg-gray-200" />
      <div className="w-full h-52 rounded-xl bg-gray-100" />
    </div>
  );

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-16 w-16 text-[#EE1D23]/70 mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 4.75V3m0 18v-1.75M6.364 6.364l-1.061-1.061m12.728 12.728l-1.061-1.061M4.75 12H3m18 0h-1.75M6.364 17.636l-1.061 1.061m12.728-12.728l-1.061 1.061"
        />
      </svg>
      <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
      <p className="text-sm text-gray-500 max-w-sm">
        No information found for the selected filters. Try refreshing or adjusting your filters.
      </p>
    </div>
  );

  return (
    <>
      {!focus && (
        <div className="w-full transition-colors duration-500">
          <div className="relative rounded-3xl shadow-2xl p-8 overflow-hidden backdrop-blur-xl bg-white/90 border border-gray-200 text-[#1A2B5C] hover:scale-[1.015] transition-transform duration-500">
            <div className="absolute -top-10 -left-10 w-60 h-60 rounded-full blur-[120px] opacity-30 bg-[#EE1D23]/20" />
            <div className="absolute -bottom-14 -right-14 w-60 h-60 rounded-full blur-[120px] opacity-30 bg-[#1A2B5C]/20" />

            <div className="relative z-10 flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="w-6 h-6 text-[#EE1D23]" />
                <h2 className="text-xl font-bold uppercase tracking-wide">Client Quality Graph</h2>
              </div>
              <button
                onClick={() => setFocus(true)}
                className="px-4 py-1.5 border border-[#1A2B5C] rounded-full text-sm font-medium hover:bg-[#1A2B5C] hover:text-white transition"
              >
                Focus Mode
              </button>
            </div>

            <div className="h-[320px] relative z-10">
              {loading ? <LoadingSkeleton /> : noData ? <NoDataMessage /> : <Bar data={data} options={options} ref={chartRef} />}
            </div>
          </div>
        </div>
      )}

      {focus && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative bg-white/90 border border-gray-300 rounded-3xl shadow-2xl w-full max-w-6xl h-[90vh] p-8 overflow-auto text-[#1A2B5C]">
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white/30 to-transparent rounded-3xl blur-2xl opacity-40" />
            <button
              onClick={() => setFocus(false)}
              className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23] hover:shadow-lg transition"
              title="Exit Focus Mode"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="w-6 h-6 text-[#1A2B5C]" />
                <h2 className="text-2xl font-bold tracking-wide">Client Quality Overview</h2>
              </div>
              <div className="h-2 w-32 rounded-full bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C]" />
            </div>

            <div className="h-[65vh]">
              {loading ? <LoadingSkeleton /> : noData ? <NoDataMessage /> : <Bar data={data} options={options} ref={chartRef} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientQualityGraph;
